import { Link } from "react-router-dom";
import { ArrowRight, Award, BookOpen, Users, GraduationCap, Sparkles } from "lucide-react";
import { BRAND, IMG, waLink } from "@/lib/brand";

export default function AboutPage() {
  return (
    <div>
      <section className="bg-[#0B192C] text-white relative overflow-hidden">
        <div className="vk-grain" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="vk-chip">About the institute</span>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl font-800 tracking-tight leading-tight">
              A coaching centre built to <span className="text-[#FF6B35]">shape futures.</span>
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed">
              {BRAND.fullName} is a Patna-based coaching centre for Class VI–X students of CBSE and ICSE boards.
              We believe every child has the potential to excel — they just need the right guidance, a disciplined
              environment, and teachers who genuinely care.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-lg overflow-hidden border border-white/10 max-w-sm ml-auto">
              <img src={IMG.classroom} alt="Vikramshila Classes classroom in Rajeev Nagar Patna" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6">
          {[
            { icon: GraduationCap, label: "Years of teaching", value: "12+", note: "CBSE & ICSE experience" },
            { icon: Users, label: "Students taught", value: "500+", note: "Class VI to X" },
            { icon: Award, label: "Faculty strength", value: "Expert team", note: "Every core subject" },
          ].map(({icon:Icon,label,value,note})=>(
            <div key={label} className="rounded-lg border border-slate-200 p-8">
              <Icon size={24} className="text-[#FF6B35]" />
              <div className="mt-4 text-xs uppercase tracking-widest text-slate-500">{label}</div>
              <div className="mt-1 font-display text-3xl font-800 text-[#0B192C]">{value}</div>
              <div className="text-sm text-slate-600 mt-1">{note}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-[#F5F7FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="vk-chip">Our philosophy</span>
          <h2 className="mt-4 font-display text-4xl font-800 text-[#0B192C]">Three pillars of every Vikramshila Classes session</h2>
          <blockquote className="mt-8 text-2xl italic text-[#0B192C] font-display leading-snug border-l-4 border-[#FF6B35] pl-6">
            &ldquo;We don&apos;t teach students to memorise — we teach them to think. When concepts become clear,
            marks become natural.&rdquo;
          </blockquote>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              { n: "01", t: "Clarity of Concept", d: "No rote learning. Every idea is broken down from first principles." },
              { n: "02", t: "Regular Practice", d: "Weekly chapter tests and monthly cumulative assessments." },
              { n: "03", t: "Individual Mentoring", d: "Because every child learns differently — and deserves attention." },
            ].map((p)=>(
              <div key={p.n} className="bg-white rounded-lg border border-slate-200 p-6">
                <div className="font-display text-3xl font-800 text-[#FF6B35]">{p.n}</div>
                <div className="mt-3 font-semibold text-[#0B192C]">{p.t}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="vk-chip">Our faculty</span>
          <h2 className="mt-4 font-display text-4xl font-800 text-[#0B192C]">Teachers who care — and know how to teach</h2>
          <p className="mt-3 max-w-2xl text-slate-600 text-lg">
            Our faculty brings together experienced subject-matter experts across Maths, Science, English, Social
            Studies, Hindi and Computer Science. Each teacher is chosen for two things: mastery of the subject and
            the patience to explain it in five different ways until the student truly gets it.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { subject: "Mathematics", note: "From foundations to board mastery" },
              { subject: "Science (PCB)", note: "Physics, Chemistry, Biology" },
              { subject: "English", note: "Grammar, literature & writing" },
              { subject: "Social Studies", note: "History, Geography, Civics" },
            ].map((f)=>(
              <div key={f.subject} className="rounded-lg border border-slate-200 p-6">
                <Sparkles size={18} className="text-[#FF6B35]" />
                <div className="mt-3 font-display text-lg font-bold text-[#0B192C]">{f.subject}</div>
                <div className="text-sm text-slate-600 mt-1">{f.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F5F7FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="vk-chip">Achievements</span>
          <h2 className="mt-4 font-display text-4xl font-800 text-[#0B192C]">Trusted by families across Patna</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { t: "50+ school toppers", d: "Across CBSE & ICSE boards" },
              { t: "95%+ scorers", d: "In Class X board exams" },
              { t: "500+ families", d: "Trust us with their children" },
              { t: "12+ years", d: "Of consistent results" },
            ].map((a)=>(
              <div key={a.t} className="rounded-lg bg-white border border-slate-200 p-6">
                <BookOpen size={20} className="text-[#FF6B35]" />
                <div className="mt-3 font-display text-xl font-bold text-[#0B192C]">{a.t}</div>
                <div className="text-sm text-slate-600 mt-1">{a.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0B192C] text-white relative overflow-hidden">
        <div className="vk-grain" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl font-800">Book a free demo class</h2>
          <p className="mt-3 text-white/70">See a Vikramshila Classes session in action before you decide.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="vk-btn-primary">Book Free Demo <ArrowRight size={16} /></Link>
            <a href={waLink(`Hi, I'd like to know more about ${BRAND.fullName}.`)} target="_blank" rel="noreferrer" className="vk-btn-outline">WhatsApp Us</a>
          </div>
        </div>
      </section>
    </div>
  );
}
